// pages/GroupAdmin.js
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../lib/api';

import Navigation from '../components/Navigation';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

// Tab components
import MembersTabs from '../components/groupAdmin/MembersTabs.jsx';
import AdminFeedTab from '../components/groupAdmin/FeedTab.jsx';
import EventsTab from '../components/groupAdmin/EventsTab.jsx';
import StoreTab from '../components/groupAdmin/StoreTab.jsx';
import OrdersTab from '../components/groupAdmin/OrdersTab.jsx';
import EmailsTab from '../components/groupAdmin/EmailsTab.jsx';
import SettingsTab from '../components/groupAdmin/SettingsTab.jsx';
import AiActionsReviewDialog from "../components/chatWidget/AiActionsReviewDialog";

import { useTranslation } from 'react-i18next';

const GroupAdmin = () => {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Only what we need for the header & stats
  const [group, setGroup] = useState(null);
  const [membersCount, setMembersCount] = useState(0);
  const [activeMembersCount, setActiveMembersCount] = useState(0);
  const [eventsCount, setEventsCount] = useState(0);
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [loading, setLoading] = useState(true);

  const fetchHeaderAndStats = async () => {
    try {
      const results = await Promise.allSettled([
        api.get(`/groups/${groupId}`),
        api.get(`/groups/${groupId}/members`),
        api.get(`/groups/${groupId}/events`),
        api.get(`/groups/${groupId}/orders`),
      ]);

      const [groupRes, membersRes, eventsRes, ordersRes] = results;

      if (groupRes.status === 'rejected') {
        const status = groupRes.reason?.response?.status;
        if (status === 403) {
          toast.error(
            t('groupAdminPage.toasts.accessDenied', {
              defaultValue: 'Access denied. Owner/Admin only.',
            })
          );
          navigate(`/groups/${groupId}/space`);
        } else {
          toast.error(
            t('groupAdminPage.toasts.loadError', {
              defaultValue: 'Failed to load admin data',
            })
          );
        }
        return;
      }

      if (groupRes.status === 'fulfilled') setGroup(groupRes.value.data);

      if (membersRes.status === 'fulfilled') {
        const members = membersRes.value.data || [];
        setMembersCount(members.length);
        setActiveMembersCount(
          members.filter((m) => m.membership_status === 'active').length
        );
      }

      if (eventsRes.status === 'fulfilled') {
        setEventsCount((eventsRes.value.data || []).length);
      }

      if (ordersRes.status === 'fulfilled') {
        const orders = ordersRes.value.data || [];
        const revenue = orders.reduce(
          (sum, o) => sum + Number(o.total_amount || 0),
          0
        );
        setTotalRevenue(revenue);
      }
    } catch (e) {
      toast.error(
        t('groupAdminPage.toasts.loadError', {
          defaultValue: 'Failed to load admin data',
        })
      );
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHeaderAndStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
        <Navigation />
        <div className="flex items-center justify-center h-96">
          <p className="text-slate-600">
            {t('loading', { defaultValue: 'Loading...' })}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <Navigation />
      <AiActionsReviewDialog groupId={group.id} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <Button
              variant="ghost"
              onClick={() => navigate(`/groups/${groupId}/space`)}
              className="mb-4"
              data-testid="back-to-group-btn"
            >
              <ArrowLeft size={16} className="mr-2" />
              {t('groupAdminPage.header.back', {
                defaultValue: 'Back to Group',
              })}
            </Button>

            <h1
              className="text-3xl font-bold text-slate-900 mb-2"
              data-testid="admin-panel-title"
            >
              {t('groupAdminPage.header.title', {
                name: group?.name || '',
                defaultValue: 'Admin Panel: {{name}}',
              })}
            </h1>
            <p className="text-slate-600">
              {t('groupAdminPage.header.subtitle', {
                defaultValue: "Manage your group's content and members",
              })}
            </p>
          </div>

          {/* AI review dialog trigger */}
          {group && <AiActionsReviewDialog groupId={group.id} />}
        </div>

        {/* Stats */}
        <div className="grid md:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-slate-600">
                {t('groupAdminPage.stats.totalMembers', {
                  defaultValue: 'Total Members',
                })}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold">{membersCount}</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-slate-600">
                {t('groupAdminPage.stats.activeMembers', {
                  defaultValue: 'Active Members',
                })}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold">{activeMembersCount}</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-slate-600">
                {t('groupAdminPage.stats.events', {
                  defaultValue: 'Events',
                })}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold">{eventsCount}</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-slate-600">
                {t('groupAdminPage.stats.totalRevenue', {
                  defaultValue: 'Total Revenue',
                })}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold">
                $
                {Number(totalRevenue || 0).toFixed(2)}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="members" className="w-full">
          <TabsList className="grid w-full grid-cols-7 mb-6">
            <TabsTrigger value="members">
              {t('groupAdminPage.tabs.members', {
                defaultValue: 'Members',
              })}
            </TabsTrigger>
            <TabsTrigger value="events">
              {t('groupAdminPage.tabs.events', {
                defaultValue: 'Events',
              })}
            </TabsTrigger>
            <TabsTrigger value="feed">
              {t('groupAdminPage.tabs.feed', {
                defaultValue: 'Feed',
              })}
            </TabsTrigger>
            <TabsTrigger value="store">
              {t('groupAdminPage.tabs.store', {
                defaultValue: 'Store',
              })}
            </TabsTrigger>
            <TabsTrigger value="orders">
              {t('groupAdminPage.tabs.orders', {
                defaultValue: 'Orders',
              })}
            </TabsTrigger>
            <TabsTrigger value="emails">
              {t('groupAdminPage.tabs.emails', {
                defaultValue: 'Emails',
              })}
            </TabsTrigger>
            <TabsTrigger value="settings">
              {t('groupAdminPage.tabs.settings', {
                defaultValue: 'Settings',
              })}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="members">
            <MembersTabs groupId={groupId} />
          </TabsContent>

          <TabsContent value="events">
            <EventsTab groupId={groupId} />
          </TabsContent>

          <TabsContent value="feed">
            <AdminFeedTab groupId={groupId} />
          </TabsContent>

          <TabsContent value="store">
            <StoreTab groupId={groupId} />
          </TabsContent>

          <TabsContent value="orders">
            <OrdersTab groupId={groupId} />
          </TabsContent>

          <TabsContent value="emails">
            <EmailsTab groupId={groupId} />
          </TabsContent>

          <TabsContent value="settings">
            <SettingsTab groupId={groupId} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default GroupAdmin;
